import { AxiosError } from 'axios';
import zod from 'zod';

import type { BasicApiError, BasicErrorData, FormApiError, FormErrorData, UnknownApiError } from './types';

export class ApiError<T extends BasicApiError | FormApiError | UnknownApiError> extends Error {
  readonly originalError;
  readonly statusCode;
  readonly type;
  readonly data;

  constructor(data: T) {
    const message = extractMessage(data);

    super(message);
    this.name = 'ApiError';
    this.originalError = data.originalError;
    this.type = data.type;
    this.statusCode = data.statusCode;
    this.data = data.data;
  }
}

/**
 * This is the ONLY place where we decide
 * what message a human should see.
 */
function extractMessage(error: BasicApiError | FormApiError | UnknownApiError): string {
  switch (error.type) {
    case 'basic':
      return error.data?.error?.message ?? error.data?.error?.code ?? 'Something went wrong';

    case 'form':
      return 'Please fix the highlighted form errors';

    case 'unknown':
    default:
      return error.data?.message ?? 'Unexpected error occurred';
  }
}

export const getStandardizedApiError = (
  error: AxiosError<unknown>,
): ApiError<BasicApiError> | ApiError<FormApiError> | ApiError<UnknownApiError> => {
  // Network / CORS / timeout errors
  if (!error.response) {
    const networkError: UnknownApiError = {
      type: 'unknown',
      statusCode: undefined,
      originalError: error,
      data: {
        message: error.message || 'Network error',
        code: error.code,
      },
    };

    return new ApiError(networkError);
  }

  const errorData = error.response.data;

  const safeErrorData: { [key: string]: unknown; message?: string; code?: string } =
    typeof errorData === 'object' && errorData !== null ? (errorData as Record<string, unknown>) : {};

  const standardizedError = {
    type: 'unknown',
    statusCode: error.response.status,
    originalError: error,
    data: safeErrorData,
  } satisfies UnknownApiError;

  if (isBasicErrorData(errorData)) {
    return new ApiError({
      ...standardizedError,
      type: 'basic',
    } as BasicApiError);
  }

  if (isFormErrorData(errorData)) {
    return new ApiError({
      ...standardizedError,
      type: 'form',
    } as FormApiError);
  }

  return new ApiError(standardizedError);
};

/* ----------------------------- Zod schemas ----------------------------- */

export const basicErrorDataSchema = zod.object({
  error: zod.object({
    code: zod.string(),
    message: zod.string().optional(),
  }),
});

const isBasicErrorData = (error: unknown): error is BasicErrorData => basicErrorDataSchema.safeParse(error).success;

export const formErrorDataSchema = zod.object({
  errors: zod.record(zod.string(), zod.array(zod.string())),
});

const isFormErrorData = (error: unknown): error is FormErrorData => formErrorDataSchema.safeParse(error).success;
